import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box, Button, Container, Typography, Chip, CircularProgress, ToggleButtonGroup, ToggleButton, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import axiosInstance from "../../../services/axiosInstance";

const PLAN_FEATURES = {
  starter: [
    "5 SIP extensions",
    "1,000 inbound minutes / period",
    "500 outbound minutes / period",
    "5 call-flow deployments / period",
    "Arabic & English IVR",
    "Call recording",
    "Voicemail inbox",
    "Basic analytics",
    "Email support",
  ],
  pro: [
    "15 SIP extensions",
    "3,000 inbound minutes / period",
    "1,500 outbound minutes / period",
    "5 call-flow deployments / period",
    "Visual flow builder",
    "AI call summaries",
    "CRM (contacts, deals, tickets)",
    "WhatsApp integration",
    "Priority support (24h)",
  ],
  ultimate: [
    "Unlimited extensions",
    "Unlimited inbound minutes",
    "Unlimited outbound minutes",
    "5 call-flow deployments / period",
    "Everything in Pro",
    "Advanced analytics",
    "Custom integrations",
    "Dedicated account manager",
    "SLA uptime guarantee",
    "24/7 priority support",
  ],
};

const PLAN_BADGES   = { pro: "Most Popular" };
const PLAN_HIGHLIGHT = { pro: true };

function calcPrice(plan, period) {
  if (period === "annual")   return Number(plan.annualPrice).toFixed(2);
  if (period === "biannual") return Number(plan.biannualPrice).toFixed(2);
  return Number(plan.monthlyPrice).toFixed(2);
}

const Offers = ({ handleActiveStep1 }) => {
  const { t }      = useTranslation("offers");
  const theme      = useTheme();
  const isLight    = theme.palette.mode === "light";
  const isEmbedded = typeof handleActiveStep1 === "function";

  const [plans,         setPlans]         = useState([]);
  // Part 4 — everything below is rendered from the live price card / plans / rate cards.
  const [pricing,       setPricing]       = useState(null);
  const [country,       setCountry]       = useState("");
  useEffect(() => {
    const params = country ? { country } : {};
    axiosInstance.get("/public/pricing", { params })
      .then(r => { setPricing(r.data); if (!country && r.data?.telephony?.country) setCountry(r.data.telephony.country); })
      .catch(() => setPricing(null));
  }, [country]);
  const fmtN = (n) => (n == null ? "—" : Number(n).toLocaleString());
  const evTok = Object.fromEntries((pricing?.events || []).map(e => [e.event, e]));
  const commonFeatures = (pricing?.featureList || []).map(k => t(`prFeat_${k}`, { defaultValue: k }));
  const [loading,       setLoading]       = useState(true);
  const [billingPeriod, setBillingPeriod] = useState("monthly");

  // On its own page (not inside signup) a plan's button opens Stripe's checkout for a signed-in business, or signup for a visitor
  // (B1.2 — it did nothing). A business Stripe already bills CHANGES its plan here (B1.3): an upgrade — a dearer plan or a longer
  // period — now, charged the difference; a cheaper plan at its renewal; a shorter period only at its renewal.
  const token    = useSelector((s) => s.auth.token);
  const navigate = useNavigate();
  const [starting, setStarting] = useState(null);
  const [notice,   setNotice]   = useState(() => {
    try { return new URLSearchParams(window.location.search).get("billing") === "cancelled" ? { severity: "info", key: "checkoutCancelled" } : null; }
    catch { return null; }
  });
  const [paying, setPaying] = useState(null);       // /billing/status of a business Stripe bills
  const [change, setChange] = useState(null);       // { plan, kind } — the confirm dialog
  const loadPaying = useCallback(() => {
    if (!token || isEmbedded) return;
    axiosInstance.get("/billing/status")
      .then(r => setPaying(r.data?.paidByStripe && ["active", "past_due", "grace", "suspended"].includes(r.data.status) ? r.data : null))
      .catch(() => setPaying(null));
  }, [token, isEmbedded]);
  useEffect(() => { loadPaying(); }, [loadPaying]);
  const MONTHS = { monthly: 1, annual: 12, biannual: 24 };
  const kindOf = (plan) => {
    const from = plans.find(p => p.id === paying?.planId);
    if (MONTHS[billingPeriod] < MONTHS[paying?.billingPeriod ?? "monthly"]) return "shorter";
    if (MONTHS[billingPeriod] > MONTHS[paying?.billingPeriod ?? "monthly"]) return "now";
    return Number(plan.monthlyPrice) < Number(from?.monthlyPrice ?? 0) ? "renewal" : "now";
  };
  const isCurrent = (plan) => paying && plan.id === paying.planId && billingPeriod === paying.billingPeriod;
  const confirmChange = async () => {
    const plan = change.plan;
    setStarting(plan.id); setNotice(null);
    try {
      const { data } = await axiosInstance.post("/billing/change-plan", { planId: plan.id, billingPeriod });
      setNotice({ severity: "success", key: data.kind === "at_renewal" ? "changeScheduled" : "changedNow",
        vars: { date: paying?.currentPeriodEnd ? new Date(paying.currentPeriodEnd).toLocaleDateString() : "" } });
      loadPaying();
    } catch (e) {
      const code = e.response?.data?.code;
      setNotice({ severity: "error", key: { payment_refused: "changeRefused", change_pending: "changePending", shorter_period: "changeShorter" }[code] || "changeFailed" });
    } finally { setStarting(null); setChange(null); }
  };
  const choosePlan = async (planId) => {
    if (isEmbedded) { handleActiveStep1(2, planId, billingPeriod); return; }
    if (!token) { navigate("/signup"); return; }
    if (paying) { const plan = plans.find(p => p.id === planId); setChange({ plan, kind: kindOf(plan) }); return; }
    setStarting(planId); setNotice(null);
    try {
      const { data } = await axiosInstance.post("/billing/checkout", { planId, billingPeriod });
      window.location.href = data.url;
    } catch (e) {
      setNotice({ severity: e.response?.data?.code === "already_subscribed" ? "info" : "error",
        key: e.response?.data?.code === "already_subscribed" ? "alreadySubscribed" : "checkoutFailed" });
      setStarting(null);
    }
  };

  useEffect(() => {
    axiosInstance.get("/plans")
      .then(r => setPlans(r.data || []))
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, []);

  const periodLabel = { monthly: t("billedMonthly"), annual: t("billedYearly"), biannual: t("billedBiannual") };

  return (
    <Box sx={{ py: isEmbedded ? 0 : { xs: 10, md: 14 }, background: isEmbedded ? "transparent" : theme.palette.background.default }}>
      <Container maxWidth="lg">

        {/* Header */}
        {!isEmbedded && (
          <Box sx={{ textAlign: "center", mb: { xs: 7, md: 9 } }}>
            <Typography variant="overline" sx={{ color: "secondary.main", fontWeight: 700, letterSpacing: "0.1em", mb: 1.5, display: "block" }}>
              {t("transparentPricing")}
            </Typography>
            <Typography variant="h2" sx={{ fontWeight: 800, fontSize: { xs: "2rem", md: "2.75rem" }, letterSpacing: "-0.025em", mb: 2 }}>
              {t("simpleTitle")}
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 480, mx: "auto", lineHeight: 1.7 }}>
              {t("simpleDesc")}
            </Typography>
          </Box>
        )}

        {isEmbedded && (
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 0.75 }}>{t("choosePlan")}</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>{t("upgradeAnytime")}</Typography>
          </Box>
        )}

        {/* Billing period toggle */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 5, flexDirection: "column", alignItems: "center", gap: 1 }}>
          <ToggleButtonGroup
            value={billingPeriod}
            exclusive
            onChange={(_, v) => v && setBillingPeriod(v)}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                px: 2.5, py: 0.875, fontSize: "0.8125rem", fontWeight: 600, borderRadius: "8px !important",
                border: `1px solid ${isLight ? "#E6E1F0" : "#272240"} !important`,
                textTransform: "none",
                "&.Mui-selected": { background: "#4F46E5", color: "#fff", borderColor: "#4F46E5 !important" },
              },
            }}>
            <ToggleButton value="monthly">{t("billing_monthly")}</ToggleButton>
            <ToggleButton value="annual">
              {t("billing_annual")}&nbsp;<Chip label="−5%" size="small" sx={{ height: 18, fontSize: "0.625rem", fontWeight: 800, background: alpha("#22C55E", 0.15), color: "#15803D", "& .MuiChip-label": { px: 0.5 } }} />
            </ToggleButton>
            <ToggleButton value="biannual">
              {t("billing_biannual")}&nbsp;<Chip label="−10%" size="small" sx={{ height: 18, fontSize: "0.625rem", fontWeight: 800, background: alpha("#22C55E", 0.15), color: "#15803D", "& .MuiChip-label": { px: 0.5 } }} />
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography sx={{ fontSize: "0.75rem", color: "text.disabled" }}>{periodLabel[billingPeriod]}</Typography>
        </Box>

        {notice && (
          <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 3, borderRadius: 2, maxWidth: 640, mx: "auto" }}>
            {t(notice.key, notice.vars)}
          </Alert>
        )}

        {/* A paying business changing its plan (B1.3): what happens, then confirm. */}
        <Dialog open={!!change} onClose={() => !starting && setChange(null)} maxWidth="xs" fullWidth
          PaperProps={{ sx: { borderRadius: 3, border: "1px solid var(--line-200)" } }}>
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>{t("changeTitle", { plan: change?.plan?.name ?? "" })}</DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
              {change?.kind === "now" ? t("changeNow")
                : change?.kind === "renewal" ? t("changeAtRenewal", { date: paying?.currentPeriodEnd ? new Date(paying.currentPeriodEnd).toLocaleDateString() : "" })
                : t("changeShorter")}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setChange(null)} disabled={!!starting} sx={{ textTransform: "none" }}>{t("cancelBtn")}</Button>
            {change?.kind !== "shorter" && (
              <Button variant="contained" onClick={confirmChange} disabled={!!starting}
                startIcon={starting ? <CircularProgress size={14} /> : null} sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}>
                {t("confirmChange")}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Plan cards */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: { xs: 3, md: 2.5 }, alignItems: "start" }}>
            {plans.map((plan) => {
              const highlight = Boolean(PLAN_HIGHLIGHT[plan.id]);
              const badge     = PLAN_BADGES[plan.id];
              const pd        = pricing?.plans?.find(p => p.id === plan.id);
              const features  = pd
                ? [
                    t("prIncludedLine", { tokens: fmtN(pd.includedTokens) }),
                    ...(pd.equivalences?.aiMinutes != null ? [t("prEquivLine", { ai: fmtN(pd.equivalences.aiMinutes), local: fmtN(pd.equivalences.localInboundMinutes), chat: fmtN(pd.equivalences.chatReplies) })] : []),
                    t("prTopupLine", { per100: fmtN(pd.topupTokensPer100), pct: pd.discountPct }),
                    t("prSeatsLine", { count: pd.seatLimit === -1 ? "∞" : pd.seatLimit, usd: pricing.extraSeatUsd }),
                    t("prAllFeatures"),
                    ...commonFeatures,
                    t("prRollover"),
                  ]
                : (PLAN_FEATURES[plan.id] || []);
              const price     = calcPrice(plan, billingPeriod);

              return (
                <Box key={plan.id} sx={{
                  position: "relative", p: { xs: 3, md: 3.5 }, borderRadius: 3,
                  border: `${highlight ? 2 : 1}px solid`,
                  borderColor: highlight ? "#4F46E5" : isLight ? "#E6E1F0" : "#272240",
                  background: highlight
                    ? isLight ? "#FDFCFF" : "#1E1B30"
                    : theme.palette.background.paper,
                  boxShadow: highlight ? "0 8px 32px rgba(79,70,229,0.16)" : "none",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: highlight ? "0 16px 48px rgba(79,70,229,0.22)" : "0 8px 24px rgba(79,70,229,0.1)",
                  },
                }}>

                  {badge && (
                    <Chip icon={<StarOutlinedIcon sx={{ fontSize: "13px !important" }} />}
                      label={t("mostPopular", { defaultValue: badge })} size="small"
                      sx={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)",
                        background: "var(--grad-brand-135)", color: "white", fontWeight: 700,
                        fontSize: "0.6875rem", "& .MuiChip-icon": { color: "rgba(255,255,255,0.7)" } }} />
                  )}

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="overline" sx={{
                      color: highlight ? "primary.main" : "text.disabled",
                      fontWeight: 700, letterSpacing: "0.08em", fontSize: "0.6875rem", mb: 0.75, display: "block",
                    }}>
                      {plan.name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mb: 0.25 }}>
                      <Typography sx={{
                        fontSize: { xs: "2.25rem", md: "2.5rem" }, fontWeight: 800,
                        color: highlight ? "primary.main" : "text.primary",
                        letterSpacing: "-0.03em", lineHeight: 1,
                      }}>
                        ${price}
                      </Typography>
                      <Typography sx={{ color: "text.secondary", fontSize: "0.875rem", fontWeight: 500 }}>{t("perMonth")}</Typography>
                    </Box>
                    {billingPeriod !== "monthly" && (
                      <Typography sx={{ fontSize: "0.75rem", color: "text.disabled" }}>
                        ${(Math.round(Number(price) * (billingPeriod === "annual" ? 12 : 24) * 100) / 100).toFixed(2)} {billingPeriod === "annual" ? t("billedYearlyShort") : t("billedBiannualShort")}
                      </Typography>
                    )}
                    <Chip icon={<BoltOutlinedIcon sx={{ fontSize: "13px !important" }} />}
                      label={plan.seatLimit === -1 ? t("unlimitedAgents") : t("nAgents", { count: plan.seatLimit })}
                      size="small" sx={{ mt: 1.25, height: 22, fontSize: "0.6875rem", fontWeight: 700,
                        background: highlight ? alpha("#4F46E5", 0.1) : alpha("#06B6D4", 0.08),
                        color: highlight ? "#4F46E5" : "#0891B2",
                        border: `1px solid ${highlight ? alpha("#4F46E5", 0.25) : alpha("#06B6D4", 0.2)}`,
                        "& .MuiChip-label": { px: 1 } }} />
                  </Box>

                  <Button fullWidth variant={highlight ? "contained" : "outlined"}
                    onClick={() => choosePlan(plan.id)} disabled={!!starting || isCurrent(plan) || (paying?.pendingPlanId === plan.id && billingPeriod === paying?.billingPeriod)}
                    startIcon={starting === plan.id ? <CircularProgress size={16} /> : null}
                    sx={{
                      mb: 3, py: 1.25, fontWeight: 700,
                      ...(highlight
                        ? { background: "var(--grad-brand-135)",
                            "&:hover": { background: "var(--grad-brand-135)", boxShadow: "0 6px 20px rgba(79,70,229,0.35)", transform: "translateY(-1px)" } }
                        : { borderColor: isLight ? "#E6E1F0" : "#272240", color: "text.primary",
                            "&:hover": { borderColor: "primary.main", background: alpha("#4F46E5", 0.06), boxShadow: "none" } }),
                    }}>
                    {!paying ? t("getStarted")
                      : isCurrent(plan) ? t("currentPlanBadge")
                      : paying.pendingPlanId === plan.id && billingPeriod === paying.billingPeriod ? t("fromRenewal")
                      : t("switchPlan")}
                  </Button>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    {features.map((f, fi) => (
                      <Box key={f} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                        <Box sx={{
                          width: 18, height: 18, borderRadius: "50%", flexShrink: 0, mt: "1px",
                          background: highlight ? alpha("#4F46E5", 0.12) : alpha("#22C55E", 0.1),
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <CheckIcon sx={{ fontSize: 11, color: highlight ? "primary.main" : "#22C55E", fontWeight: 700 }} />
                        </Box>
                        <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.5, fontSize: "0.875rem" }}>
                          {t(`feat_${plan.id}_${fi}`, { defaultValue: f })}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {pricing && (
          <Box sx={{ mt: 8, display: "grid", gap: 4 }}>
            {/* What things cost */}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 0.5 }}>{t("prCostTitle")}</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>{t("prCostDesc", { usd: pricing.tokenUsd })}</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5 }}>
                {(pricing.events || []).filter(e => e.event !== "telephony_min").map(e => (
                  <Box key={e.event} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: isLight ? "#E6E1F0" : "#272240" }}>
                    <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 0.5 }}>{t(`prEv_${e.event}`, { defaultValue: e.event })}</Typography>
                    {e.passThrough === "platform_cost"
                      ? <Typography sx={{ fontSize: "0.95rem", fontWeight: 700 }}>{t("prCostPlusMargin")}</Typography>
                      : <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{fmtN(e.tokens)} <span style={{ fontSize: "0.8rem", fontWeight: 600 }}>{t("prTokens")}</span></Typography>}
                    {e.passThrough !== "none" && e.passThrough !== "platform_cost" && <Typography sx={{ fontSize: "0.7rem", color: "text.disabled" }}>{t("prPlusDelivery")}</Typography>}
                  </Box>
                ))}
                <Box sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: isLight ? "#E6E1F0" : "#272240" }}>
                  <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 0.5 }}>{t("prEv_telephony_min")}</Typography>
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: 700 }}>{t("prTelephonyDerived")}</Typography>
                </Box>
              </Box>
            </Box>

            {/* Telephony by country */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>{t("prTelTitle")}</Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>{t("prTelDesc")}</Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>{t("prCountry")}</InputLabel>
                  <Select label={t("prCountry")} value={country} onChange={e => setCountry(e.target.value)} MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}>
                    {(pricing.countries || []).map(c => <MenuItem key={c.iso} value={c.iso}>{c.name} ({c.iso})</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
              {pricing.telephony?.rows?.length ? (
                <Box sx={{ overflowX: "auto", borderRadius: 2, border: "1px solid", borderColor: isLight ? "#E6E1F0" : "#272240" }}>
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell sx={{ fontSize: "0.72rem", fontWeight: 700 }}>{t("prRoute")}</TableCell>
                      {pricing.plans.map(p => <TableCell key={p.id} align="right" sx={{ fontSize: "0.72rem", fontWeight: 700 }}>{p.name}</TableCell>)}
                    </TableRow></TableHead>
                    <TableBody>
                      {pricing.telephony.rows.map(r => (
                        <TableRow key={r.routeKey}>
                          <TableCell sx={{ fontSize: "0.82rem" }}>{t(`prDir_${r.direction}`)} · {t(`prType_${r.numberType}`)}{r.rateMissing ? <span style={{ opacity: 0.5 }}> · {t("prNoRate")}</span> : null}</TableCell>
                          {pricing.plans.map(p => <TableCell key={p.id} align="right" sx={{ fontSize: "0.82rem", fontVariantNumeric: "tabular-nums" }}>{r.perPlan[p.id] ? `${fmtN(Math.round(r.perPlan[p.id].tokensPerMin * 10) / 10)} ${t("prPerMin")}` : "—"}</TableCell>)}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : <Typography variant="body2" sx={{ color: "text.disabled" }}>{t("prPickCountry")}</Typography>}
              <Typography sx={{ fontSize: "0.72rem", color: "text.disabled", mt: 1 }}>{t("prTelNote", { m: Math.round(pricing.marginDefault * 100) })}</Typography>
            </Box>

            {/* Worked examples */}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1.5 }}>{t("prExTitle")}</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: `repeat(${Math.max(1, pricing.plans.length)}, 1fr)` }, gap: 1.5 }}>
                {pricing.plans.map(p => { const ex = pricing.examples?.[p.id]; return (
                  <Box key={p.id} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: isLight ? "#E6E1F0" : "#272240" }}>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "text.secondary", mb: 1 }}>{p.name}</Typography>
                    {ex && [
                      ["prExAi", ex.aiCall3minLocal], ["prExIvr", ex.ivrCall3minLocal], ["prExWa", ex.whatsapp500Replies], ["prExOut", ex.outboundMobile3min],
                    ].map(([k, v]) => v != null && (
                      <Box key={k} sx={{ display: "flex", justifyContent: "space-between", gap: 1, fontSize: "0.82rem", py: 0.5, borderBottom: "1px dashed", borderColor: isLight ? "#EEE" : "#333" }}>
                        <span>{t(k)}</span><b style={{ fontVariantNumeric: "tabular-nums" }}>{fmtN(v)} {t("prTokens")}</b>
                      </Box>
                    ))}
                  </Box>
                ); })}
              </Box>
            </Box>

            {/* How it works */}
            <Box sx={{ p: 3, borderRadius: 3, background: isLight ? alpha("#4F46E5", 0.04) : alpha("#4F46E5", 0.12) }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{t("prHowTitle")}</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { fontSize: "0.875rem", lineHeight: 1.7, color: "text.secondary" } }}>
                <li>{t("prHow1")}</li>
                <li>{t("prHow2", { tokens: fmtN(pricing.failoverTokens) })}</li>
                <li>{t("prHow3")}</li>
                <li>{t("prHow4", { usd: pricing.tokenUsd })}</li>
              </Box>
            </Box>
          </Box>
        )}

        {!isEmbedded && (
          <Typography variant="body2" sx={{ textAlign: "center", mt: 5, color: "text.disabled" }}>
            {pricing ? t("prFooter") : t("footer")}
          </Typography>
        )}
      </Container>
    </Box>
  );
};

export default Offers;
